/**
 * Shared Markdown utilities for Notes views
 */

import { escapeAttr } from "../../../../engine/core/validation.js";

/**
 * Convert plain URLs to clickable links (but not already inside href or anchor tags)
 */
function linkifyUrls(html) {
  // Split by existing anchor tags to avoid double-linking
  const parts = html.split(/(<a [^>]*>.*?<\/a>)/g);
  return parts
    .map((part) => {
      // Skip if this is already an anchor tag
      if (part.startsWith("<a ")) return part;
      // Convert plain URLs to links
      return part.replace(
        /(https?:\/\/[^\s<>"']+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
      );
    })
    .join("");
}

/**
 * Render markdown content to HTML
 * @param {string} content - Raw markdown content
 * @param {Function} escapeHtml - Function to escape HTML entities
 * @returns {string} HTML string
 */
export function renderMarkdown(content, escapeHtml) {
  if (!escapeHtml) throw new Error('escapeHtml is required');
  const escape = escapeHtml;

  let html = escape(content)
    // Headers
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Code blocks
    .replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    // Blockquotes
    .replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>")
    // Markdown links (only allow safe URL schemes)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
      const trimmed = url.trim().toLowerCase();
      if (trimmed.startsWith('https://') || trimmed.startsWith('http://') || trimmed.startsWith('mailto:')) {
        return `<a href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
      return `${text} (${url})`;
    })
    // Checkboxes
    .replace(
      /^\- \[x\] (.+)$/gm,
      '<li><input type="checkbox" checked disabled> $1</li>',
    )
    .replace(
      /^\- \[ \] (.+)$/gm,
      '<li><input type="checkbox" disabled> $1</li>',
    )
    // Lists
    .replace(/^\- (.+)$/gm, "<li>$1</li>")
    .replace(/^\d+\. (.+)$/gm, "<li>$1</li>")
    // Horizontal rule
    .replace(/^---$/gm, "<hr>")
    // Paragraphs
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br>");

  // Convert plain URLs to links (after markdown links are processed)
  html = linkifyUrls(html);

  html = "<p>" + html + "</p>";
  html = html.replace(/<p><\/p>/g, "");

  return html;
}
